import React from 'react';
import { motion } from 'motion/react';
import { useAppContext } from '../AppContext';
import { Toast as ToastType } from '../ToastContext';

interface ToastProps {
    toast: ToastType;
    onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
    const { appConfig } = useAppContext();

    const isSuccess = toast.type === 'success';

    return (
        <motion.div
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className={`flex items-center gap-4 max-w-sm w-[90vw] p-4 rounded-2xl border shadow-2xl backdrop-blur-md pointer-events-auto ${
                isSuccess
                    ? 'bg-[var(--primary-dark-purple)]/95 border-[var(--accent-pink)] text-white shadow-[var(--accent-pink)]/20'
                    : 'bg-red-950/95 border-red-500/50 text-white shadow-red-500/10'
            }`}
        >
            {/* Jumping & Spinning Logo Icon */}
            <motion.div
                className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-white/10 rounded-full p-1.5 overflow-hidden"
                animate={{
                    y: [0, -14, 0, -10, 0, -4, 0],
                    rotate: [0, 360, 360, 0, 360],
                }}
                transition={{
                    duration: 1.8,
                    ease: "easeInOut",
                    repeat: Infinity,
                    repeatDelay: 0.6
                }}
            >
                {appConfig?.logoUrl ? (
                    <img
                        src={appConfig.logoUrl}
                        alt="Logo"
                        className="w-full h-full object-contain filter drop-shadow"
                        referrerPolicy="no-referrer"
                    />
                ) : (
                    <i className={`ph-duotone ${isSuccess ? 'ph-check-circle text-2xl text-[var(--accent-pink)]' : 'ph-warning text-2xl text-red-400'}`} />
                )}
            </motion.div>

            {/* Message Body */}
            <div className="flex-1 min-w-0">
                <div className={`text-[10px] font-extrabold uppercase tracking-widest ${isSuccess ? 'text-[var(--accent-yellow)]' : 'text-red-400'}`}>
                    {isSuccess ? 'Transaction Processed!' : 'Purchase Failed'}
                </div>
                <p className="text-xs text-white/90 font-medium leading-relaxed mt-0.5 break-words">
                    {toast.message}
                </p>
            </div>

            {/* Close Button */}
            <button
                onClick={() => onClose(toast.id)}
                className="flex-shrink-0 p-1 rounded-full text-white/40 hover:text-white/80 hover:bg-white/10 transition-all"
                aria-label="Close notification"
            >
                <i className="ph-duotone ph-x text-base" />
            </button>
        </motion.div>
    );
};

export default Toast;
